import type { ProjectTaskAssignee, TimelineEvent } from '@/frontend/features/Fde/types'

function eventZone(event: TimelineEvent) {
  if (!event.all_day) return undefined
  try {
    new Intl.DateTimeFormat('en', { timeZone: event.time_zone })
    return event.time_zone
  } catch {
    return 'UTC'
  }
}

export function calendarDateKey(date: Date, timeZone?: string) {
  const parts = new Intl.DateTimeFormat('en', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).formatToParts(date)
  const part = (type: string) => parts.find((item) => item.type === type)!.value
  return `${part('year')}-${part('month')}-${part('day')}`
}

export function eventOccursOn(event: TimelineEvent, day: string) {
  if (event.status === 'cancelled') return false
  // All-day dates belong to the source calendar, not the viewer's timezone.
  // End timestamps are exclusive, so midnight never adds an extra day.
  const zone = eventZone(event)
  const start = calendarDateKey(new Date(event.starts_at), zone)
  const end = calendarDateKey(new Date(new Date(event.ends_at).getTime() - 1), zone)
  return day >= start && day <= end
}

export function eventMatchesFilters(event: TimelineEvent, query: string, person?: ProjectTaskAssignee) {
  if (person) {
    const assigned = person.type === 'client'
      ? event.elements.some((item) => item.id === person.id)
      : person.type !== 'agent' && (event.manager_user_id === person.id || event.users.some((item) => item.id === person.id))
    if (!assigned) return false
  }
  return [event.summary, event.description, event.location, event.manager_name, ...event.users.map((item) => item.name), ...event.elements.map((item) => item.name)]
    .join(' ').toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
}

export function eventTimeLabel(event: TimelineEvent, allDayLabel: string) {
  if (event.all_day) return allDayLabel
  const format = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${format.format(new Date(event.starts_at))}–${format.format(new Date(event.ends_at))}`
}

export function eventDateLabel(event: TimelineEvent) {
  const format = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: eventZone(event) })
  const first = format.format(new Date(event.starts_at))
  const last = format.format(new Date(new Date(event.ends_at).getTime() - 1))
  return first === last ? first : `${first} – ${last}`
}
